import React from 'react';
import cx from 'classnames';
import { compact } from 'lodash';
import { Alert, Col, ControlLabel, Row } from 'react-bootstrap';

import RadioGroup from '@magoosh/lib/formik_inputs/field_radio_group';
import { toSentence } from 'utilities/formatting';
import { formatPrice } from 'utilities/money';

import BillingZipCode from '@payments/components/inputs/billing_zip_code';
import StripeCardInputs from '@payments/components/inputs/stripe_card';
import { PaymentMethod, PaymentStyle } from '@payments/legacy_payments/types';

import stripeLogo from '@payments/components/form_groups/payment_methods/assets/powered_by_stripe.svg';
import style from '@payments/style.module.scss';

interface Props {
  allowedPaymentMethods?: PaymentMethod[];
  onPaymentMethodChanged?: (paymentMethod: string) => void;
  onZipCodeChanged?: (zipCode: string) => void;
  paymentMethod: PaymentMethod;
  paymentRequestMethod?: string;
  paymentStyle: PaymentStyle;
  totalExcludingTax: number;
}

export const PaymentMethodsFormGroup: React.FC<Props> = ({
  allowedPaymentMethods,
  onPaymentMethodChanged,
  onZipCodeChanged,
  paymentMethod,
  paymentRequestMethod,
  paymentStyle,
  totalExcludingTax
}) => {
  const paymentMethods = allowedPaymentMethods || [
    PaymentMethod.STRIPE,
    PaymentMethod.PAYPAL,
    PaymentMethod.ALIPAY,
    PaymentMethod.STRIPE_PAYMENT_REQUEST
  ];

  const disabledPaymentsHint = () => {
    const disabledPaymentMethodNames = paymentMethods.filter((method) => method !== PaymentMethod.STRIPE).map((paymentMethod) => {
      if (paymentMethod === PaymentMethod.PAYPAL) {
        return 'PayPal';
      } else if (paymentMethod === PaymentMethod.ALIPAY) {
        return 'Alipay';
      } else if (paymentMethod === PaymentMethod.STRIPE_PAYMENT_REQUEST) {
        return paymentRequestMethod;
      } else {
        return paymentMethod;
      }
    });

    return `Select “Pay full ${formatPrice(totalExcludingTax)} today” to pay with ${toSentence(compact(disabledPaymentMethodNames), 'or')}`;
  }

  const paymentOptions = [
    {
      icons: ['fa fa-credit-card-alt'],
      value: PaymentMethod.STRIPE
    },
    {
      icons: ['fa fa-cc-paypal'],
      value: PaymentMethod.PAYPAL,
      disabled: paymentStyle === PaymentStyle.INSTALLMENTS,
      hint: paymentStyle === PaymentStyle.INSTALLMENTS && disabledPaymentsHint()
    },
    {
      icons: ['sprite sprite-alipay subscription-payment-sprite'],
      value: PaymentMethod.ALIPAY,
      disabled: paymentStyle === PaymentStyle.INSTALLMENTS
    }
  ].filter((method) => paymentMethods.indexOf(method.value) !== -1);

  // Add Apple/Google pay if available
  if (paymentRequestMethod && paymentMethods.indexOf(PaymentMethod.STRIPE_PAYMENT_REQUEST) !== -1) {
    let iconName = 'sprite';
    if (paymentRequestMethod == 'Google Pay') {
      iconName += ' sprite-google-pay';
    } else if (paymentRequestMethod == 'Apple Pay') {
      iconName += ' sprite-apple-pay';
    }

    paymentOptions.push({
      icons: [iconName],
      value: PaymentMethod.STRIPE_PAYMENT_REQUEST,
      disabled: paymentStyle === PaymentStyle.INSTALLMENTS
    });
  }

  return (
    <div className="form-group">
      <fieldset className="inputs">
        <legend className="u-margin-B-n">
          <span>Payment Method</span>
        </legend>
        <Row>
          <Col md={8}>
            {
              paymentMethods.length > 1 && (
                <RadioGroup
                  name="paymentMethod"
                  onChangeCondition={(newPaymentMethod: string) => (
                    onPaymentMethodChanged && onPaymentMethodChanged(newPaymentMethod)
                  )}
                  optionsArray={paymentOptions}
                  optionClassName={cx(style.payments, 'radio-inline')}
                  value={paymentMethod}

                />
              )
            }

            {
              paymentMethod === PaymentMethod.STRIPE && (
                <>
                  <StripeCardInputs />

                  {/* The billing zip used to sit inside the card inputs; it is its own field now,
                      so this flow renders the pair the way the current one does. */}
                  <BillingZipCode
                    onZipCodeChanged={(zipCode) => (
                      onZipCodeChanged && onZipCodeChanged(zipCode)
                    )}
                  />
                </>
              )
            }

            {paymentMethod === PaymentMethod.PAYPAL && (
              <Alert bsStyle="secondary">
                Payment will be collected on PayPal after you click &quot;Continue to Payment&quot;
              </Alert>
            )}

            {paymentMethod === PaymentMethod.ALIPAY && (
              <Alert bsStyle="secondary">
                Payment will be collected on Alipay after you click &quot;Continue to Payment&quot;
              </Alert>
            )}
          </Col>
          {
            paymentMethod === PaymentMethod.STRIPE && (
              <Col md={4} className="hidden-sm hidden-xs text-center u-margin-T-m">
                <a href="http://www.stripe.com" target="_blank" rel="noopener noreferrer">
                  <img alt="Powered by Stripe" src={stripeLogo}></img>
                </a>
              </Col>
            )
          }
        </Row>
      </fieldset>
    </div>
  );
};

export default PaymentMethodsFormGroup;
